import { useTu } from "@src/appV2/i18n/useTu";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

import {
  Box,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { InfoTooltip } from "@src/appV2/redesign/components/InfoTooltip";
import { Text } from "@src/appV2/redesign/components/Text";
import { ALL_NOTIFICATIONS_LABEL, HOME_HEALTH_ALERT_ROWS, MASTER_ALERT_KEY } from "./constants";
import { type HomeHealthAlertEnabledMap } from "./types";

interface HomeHealthNotificationPreferencesViewProps {
  enabledByKey: HomeHealthAlertEnabledMap;
  masterEnabled: boolean;
  savingKey: string | undefined;
  isLoading: boolean;
  onToggleRow: (alertKey: string, enabled: boolean) => void;
  onToggleAll: (enabled: boolean) => void;
}

const COLUMN_WIDTH = 96;

function SmsPlaceholder() {
  return (
    <Text
      inline
      variant="caption"
      sx={(theme) => ({ color: theme.palette.text.secondary })}
      data-testid="notification-blank"
    >
      —
    </Text>
  );
}

interface EmailCellProps {
  ariaLabel: string;
  checked: boolean;
  isSaving: boolean;
  isDisabled: boolean;
  showStaticOff: boolean;
  onChange: (enabled: boolean) => void;
}

function EmailCell(props: EmailCellProps) {
  const { tu } = useTu("facilityUser");

  const { ariaLabel, checked, isSaving, isDisabled, showStaticOff, onChange } = props;

  if (showStaticOff) {
    return (
      <Text inline variant="caption" data-testid="uneditable-notification-option">
        {tu("Off")}
      </Text>
    );
  }

  if (isSaving) {
    return <CircularProgress size={20} />;
  }

  return (
    <Switch
      aria-label={ariaLabel}
      checked={checked}
      disabled={isDisabled}
      size="small"
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}

export function HomeHealthNotificationPreferencesView(
  props: HomeHealthNotificationPreferencesViewProps
) {
  const { tu } = useTu("facilityUser");

  const { enabledByKey, masterEnabled, savingKey, isLoading, onToggleRow, onToggleAll } = props;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const isMasterSaving = savingKey === MASTER_ALERT_KEY;
  const isAnyRowSaving = savingKey !== undefined;

  return (
    <TableContainer>
      <Table size="small" aria-label={tu("Home health notification preferences")}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Text inline semibold variant="caption">
                {tu("Notification")}
              </Text>
            </TableCell>
            <TableCell align="center" sx={{ width: COLUMN_WIDTH }}>
              <Text inline semibold variant="caption">
                {tu("Email")}
              </Text>
            </TableCell>
            <TableCell align="center" sx={{ width: COLUMN_WIDTH }}>
              <Text inline semibold variant="caption">
                SMS
              </Text>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow
            sx={(theme) => ({
              backgroundColor: theme.palette.background?.muted,
            })}
          >
            <TableCell>
              <Text inline bold variant="body2">
                {ALL_NOTIFICATIONS_LABEL}
              </Text>
            </TableCell>
            <TableCell align="center" sx={{ width: COLUMN_WIDTH }}>
              <EmailCell
                ariaLabel={ALL_NOTIFICATIONS_LABEL}
                checked={masterEnabled}
                isDisabled={isAnyRowSaving && !isMasterSaving}
                isSaving={isMasterSaving}
                showStaticOff={false}
                onChange={onToggleAll}
              />
            </TableCell>
            <TableCell align="center" sx={{ width: COLUMN_WIDTH }}>
              <SmsPlaceholder />
            </TableCell>
          </TableRow>

          {HOME_HEALTH_ALERT_ROWS.map((row) => {
            const isThisRowSaving = savingKey === row.alertKey;
            return (
              <TableRow key={row.alertKey}>
                <TableCell>
                  <Stack alignItems="center" direction="row" spacing={1}>
                    <Text inline variant="body2">
                      {row.label}
                    </Text>
                    <InfoTooltip title={row.tooltip}>
                      <InfoOutlined
                        aria-label={`${row.label} info`}
                        sx={(theme) => ({
                          fontSize: 16,
                          color: theme.palette.text.secondary,
                          cursor: "help",
                        })}
                      />
                    </InfoTooltip>
                  </Stack>
                </TableCell>
                <TableCell align="center" sx={{ width: COLUMN_WIDTH }}>
                  <EmailCell
                    ariaLabel={`Email notification for ${row.label}`}
                    checked={enabledByKey[row.alertKey] ?? true}
                    isDisabled={isAnyRowSaving && !isThisRowSaving}
                    isSaving={isThisRowSaving}
                    showStaticOff={!masterEnabled}
                    onChange={(enabled) => onToggleRow(row.alertKey, enabled)}
                  />
                </TableCell>
                <TableCell align="center" sx={{ width: COLUMN_WIDTH }}>
                  <SmsPlaceholder />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
